import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Icon from './DashboardIcons';
import { getVisibleNavigation } from '../../config/organizationNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const GROUP_META = {
  players: { label: 'اللاعبون', icon: 'users', href: (id) => `/players?q=` },
  employees: { label: 'الموظفون', icon: 'briefcase', href: (id) => `/staff-admin?q=` },
  teams: { label: 'الفرق', icon: 'flag', href: (id) => `/teams?q=` },
  invoices: { label: 'الفواتير', icon: 'document', href: (id) => `/finance?tab=invoices` },
};

export const GlobalSearchTrigger = ({ user }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="global-search-trigger-btn"
        title="بحث شامل (Ctrl+K)"
        className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-all hover:bg-slate-100/80 hover:text-teal-700 active:scale-90"
      >
        <Icon name="search" className="h-[17px] w-[17px]" />
      </button>
      {open && <GlobalSearchModal user={user} onClose={() => setOpen(false)} />}
    </>
  );
};

function GlobalSearchModal({ user, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const navItems = getVisibleNavigation(user).flatMap((g) => g.items);
  const navMatches = query.trim() ? navItems.filter((i) => i.name.includes(query.trim())).slice(0, 5) : [];

  const runSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults(null); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try { setResults((await axios.get(`${API}/search/global`, { ...auth(), params: { q } })).data); }
      catch { setResults(null); }
      setLoading(false);
    }, 300);
  }, []);

  const onChange = (e) => { const v = e.target.value; setQuery(v); runSearch(v); };

  const goTo = (href) => { onClose(); navigate(href); };

  const entries = results ? Object.entries(results).filter(([, items]) => items.length > 0) : [];
  const hasAny = navMatches.length > 0 || entries.length > 0;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 p-4 pt-24" onClick={onClose} data-testid="global-search-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
          <Icon name="search" className="w-4 h-4 shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={onChange}
            placeholder="بحث عن لاعب، موظف، فريق، فاتورة، أو قسم..."
            data-testid="global-search-input"
            className="w-full bg-transparent text-sm font-medium text-slate-800 placeholder-slate-400 outline-none"
          />
          <button onClick={onClose} data-testid="global-search-close-btn" className="shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2" data-testid="global-search-results">
          {loading && <div className="px-3 py-4 text-center text-xs text-slate-400">جارٍ البحث...</div>}
          {!loading && query.trim().length >= 2 && !hasAny && (
            <div className="px-3 py-6 text-center text-sm text-slate-400" data-testid="global-search-empty">لا نتائج مطابقة لـ«{query}»</div>
          )}
          {navMatches.length > 0 && (
            <div className="mb-1">
              <div className="px-3 py-1.5 text-[11px] font-bold uppercase text-slate-400">الأقسام</div>
              {navMatches.map((m) => (
                <button key={m.href} onClick={() => goTo(m.href)} data-testid="global-search-nav-item" className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-800">
                  <Icon name={m.icon} className="w-4 h-4 text-teal-500" /> {m.name}
                </button>
              ))}
            </div>
          )}
          {entries.map(([key, items]) => {
            const meta = GROUP_META[key];
            if (!meta) return null;
            return (
              <div key={key} className="mb-1">
                <div className="px-3 py-1.5 text-[11px] font-bold uppercase text-slate-400">{meta.label}</div>
                {items.map((it) => (
                  <button key={it.id} onClick={() => goTo(`${meta.href(it.id)}${encodeURIComponent(it.label || '')}`)} data-testid="global-search-result-item" className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-right text-sm hover:bg-teal-50">
                    <Icon name={meta.icon} className="w-4 h-4 shrink-0 text-teal-500" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold text-slate-800">{it.label}</span>
                      <span className="block truncate text-xs text-slate-400">{it.sublabel}</span>
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
          {!query.trim() && (
            <div className="px-3 py-6 text-center text-xs text-slate-400">اكتب حرفين على الأقل للبحث في اللاعبين والموظفين والفرق والفواتير والأقسام</div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
